import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useToast } from './ToastContext';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const API_BASE = '/api';

interface PolicyRule {
  label: string;
  test: (pw: string) => boolean;
}

const EyeIcon = FaEye as unknown as React.FC<{ className?: string }>;
const EyeSlashIcon = FaEyeSlash as unknown as React.FC<{ className?: string }>;

const POLICY_RULES: PolicyRule[] = [
  { label: 'At least 12 characters', test: (pw) => pw.length >= 12 },
  { label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { label: 'One number', test: (pw) => /[0-9]/.test(pw) },
  { label: 'One special character', test: (pw) => /[!@#$%^&*()\-_=+[\]{};:'",.<>/?\\|`~]/.test(pw) },
];

function SetupAccountPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<{ name: string; email: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (!token) {
      setTokenError('No setup link token found in the URL.');
      setVerifying(false);
      return;
    }

    fetch(`${API_BASE}/account-setup/verify?token=${encodeURIComponent(token)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.valid) {
          setTokenValid(true);
          setUserInfo({ name: data.name, email: data.email });
        } else {
          setTokenError(data.error || 'This link is not valid.');
        }
      })
      .catch(() => setTokenError('Could not verify this link.'))
      .finally(() => setVerifying(false));
  }, [token]);

  const allRulesPass = POLICY_RULES.every((rule) => rule.test(password));
  const passwordsMatch = password.length > 0 && password === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allRulesPass || !passwordsMatch) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const res = await fetch(`${API_BASE}/account-setup/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.details ? data.details.join(' ') : data.error || 'Something went wrong');
      }

      setSuccess(true);
      showToast('Password set successfully. Redirecting to login...');
      setTimeout(() => navigate('/admin'), 2500);
    } catch (err: any) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return <div className="app"><p>Verifying link...</p></div>;
  }

  if (!tokenValid) {
    return (
      <div className="app">
        <h1>Link Not Valid</h1>
        <p className="error">{tokenError}</p>
        <p>Please contact your administrator for a new link.</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="app">
        <h1>Password Set</h1>
        <p>Your password has been set. Redirecting you to login...</p>
      </div>
    );
  }

    return (
    <div className="login-page">
      <div className="modal setup-account-modal">
        <h1>Set Up Your Account</h1>
        <p>Welcome, {userInfo?.name}. Choose a password for {userInfo?.email}.</p>

        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label>New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                className="invite-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <div className="form-field">
            <label>Confirm Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className="invite-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword((s) => !s)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
              </button>
            </div>
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="error">Passwords do not match.</p>
            )}
          </div>

          <ul className="policy-checklist">
            {POLICY_RULES.map((rule) => {
              const passed = rule.test(password);
              return (
                <li key={rule.label} className={passed ? 'policy-pass' : 'policy-fail'}>
                  {passed ? '✓' : '○'} {rule.label}
                </li>
              );
            })}
          </ul>

          {submitError && <p className="error">{submitError}</p>}

          <button
            type="submit"
            className="btn-primary btn-full-width"
            disabled={submitting || !allRulesPass || !passwordsMatch}
          >
            {submitting ? 'Setting Password...' : 'Set Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SetupAccountPage;